import { fromJS, List, Map, OrderedSet } from "immutable";
import { isEqual, cloneDeep, unset, get, set, isEmpty, isNull, isNumber, hasIn, memoize } from "lodash";
import { SUB_PROP_GROUPS, MERGE_PROPS } from "@showitinc/merge-utils";
import AppConstants from "../constants/AppConstants";

import { ensureArray } from "./JSUtils";
import {
  canPropInherit,
  prepForInherit,
  getProp,
  isInheritedPropSynced,
  shouldPropInherit,
} from "./Merge/MergeUtils.mjs";

const {
  BG_PROPERTY_FILL_TYPE,
  BG_PROPERTY_BG_COLOR,
  BG_TYPE_COLOR,
  LAYOUT_DESKTOP: DESKTOP,
  LAYOUT_MOBILE: MOBILE,
  SYNCED_PROPS,
  SYNCED_PROPS_PLACEHOLDER,
  THEME_BG_PROPERTY_COLOR,
  UPDATE_ACTION_DELETE,
} = AppConstants;

const PLACEHOLDER = { value: SYNCED_PROPS_PLACEHOLDER, name: SYNCED_PROPS };

/**
 * @typedef {import("./Merge/MergeUtils.mjs").SyncableEntity} SyncableEntity
 * @typedef {import("../vo/BlockUpdate.mjs").PropUpdate} PropUpdate
 * @typedef {import("immutable").Map} ImmutableMap
 */

/**
 * Callback returned by toggleSyncedPropMultiple. Aligns every prop's sync status to the first prop's.
 * @callback ToggleSyncedPropMultipleCallback
 * @param {String[]} props - List of prop names to make sure are all synced or not synced.
 * @param {String=} initialPathPrefix - Prefix for prop name
 * @param {(String|Object)=} mergeValueOverride - Value that overwrites value from propContainer global state
 * @returns {PropUpdate[] | undefined}
 */

/**
 * Takes a string and splits it to an array, returns an empty array if nestedPropPath is falsey.
 * @param {String=} nestedPropPath
 * @returns {String[]}
 */
const getNestedPropPathArray = (nestedPropPath) =>
  nestedPropPath ? nestedPropPath.split(".") : [];

/**
 * Gets synced props and relevant sub props if the propName is found in the propContainer's sync array.
 * @param {Object} propContainer -
 * @returns
 */
export const getSyncedProps = (propContainer) => {
  const syncedProps = {};
  const synced = propContainer.get(SYNCED_PROPS)?.toJS() || [];

  synced.forEach((prop) => {
    const propPath = prop.split(".");
    set(syncedProps, propPath, propContainer.getIn([DESKTOP, ...propPath]));
    SUB_PROP_GROUPS[prop]?.forEach((subProp) => {
      syncedProps[subProp] = propContainer.getIn([DESKTOP, subProp]);
    });
  });
  return syncedProps;
};

/**
 * Checks the container's sync array for the prop's sync state
 * @param {ImmutableMap | Object} propContainer
 * @param {String} prop - Name of prop to check
 * @param {String=} layout
 * @param {(Object | Map)=} inheritFrom
 * @returns {Boolean}
 */
export const isSynced = (propContainer, prop, layout, inheritFrom) => {
  if (inheritFrom && layout && canPropInherit(propContainer, prop)) {
    const otherLayout = layout === DESKTOP ? MOBILE : DESKTOP;
    if (
      getProp(propContainer, layout, prop) === undefined &&
      getProp(propContainer, otherLayout, prop) === undefined &&
      !isSynced(propContainer, prop)
    ) {
      // not prepForInherit: its item carries values only, not the theme's sync state
      return isInheritedPropSynced(propContainer, layout, prop, inheritFrom);
    }
  }

  const sync = Map.isMap(propContainer)
    ? propContainer.get(SYNCED_PROPS, [])
    : propContainer[SYNCED_PROPS] || [];

  return (Array.isArray(sync) || List.isList(sync)) && sync.indexOf(prop) !== -1;
};

/**
 * @typedef {Object} Update
 * @property {String} name - Name of the property to update
 * @property {any} value - Update value
 * @property {String|Boolean=} action - Flag for the updater
 *
 * Rolls toggleSyncedProp merge updates into updates that don't overwrite eachother.
 * @param {Update[]} propUpdates
 * @returns {Update[]}
 */
export const formatSyncUpdates = (propUpdates) => {
  // splits update by type and flag to replace the placeholder
  const [mergeUpdates, syncUpdates, placeholder] = propUpdates.reduce(
    (acc, current) => {
      const { value, name, action } = current;
      if (name === SYNCED_PROPS && value === "placeholder") {
        acc[2] = [PLACEHOLDER];
      } else {
        const index = Number(name === SYNCED_PROPS && action === "set");
        acc[index].push(current);
      }
      return acc;
    },
    [[], []]
  );

  const conjoinedSyncUpdate = placeholder || [];
  // prevents value of sync from being overwritten when more than one prop is synced
  syncUpdates.length &&
    conjoinedSyncUpdate.push(
      syncUpdates.reduce((acc, { value }) => {
        acc.value = acc.value.concat(value);
        return acc;
      })
    );

  return [...mergeUpdates, ...conjoinedSyncUpdate];
};

/**
 * Copies synced props to mobile in a tree of any depth, returning a readonly unmerged immutable and object design in an array.
 * @param {Object | ImmutableMap} design - Design map
 * @returns {[ImmutableMap, Object]} - [JS object of merged design, immutable map of merged design]
 */
export const recursivelyCopySyncedPropsToMobile = (design) => {
  const mergedDesign = Map.isMap(design) ? design.toJS() : cloneDeep(design);
  const traverseDesign = (designPart) => {
    /* eslint-disable */
    for (const key in designPart) {
      if (key === "sync") {
        designPart[key].forEach((syncedProp) => {
          const propPath = syncedProp?.split(".") || [];
          const desktopValue = get(designPart, [DESKTOP, ...propPath]);
          // don't write undefined value to key in sync props if undefined in desktop
          desktopValue === undefined
            ? unset(designPart, [MOBILE, ...propPath])
            : set(designPart, [MOBILE, ...propPath], desktopValue);
        });
      }
      if (typeof designPart[key] === "object" && !Array.isArray(designPart[key])) {
        traverseDesign(designPart[key]);
      }
    }
  };
  /* eslint-disable */
  traverseDesign(mergedDesign);
  return [fromJS(mergedDesign), mergedDesign];
};

/**
 * Recursively merges/syncs props. This will store all sub props in dot notation ex. "link.tD.whatever"
 * @param {Object | ImmutableMap} design
 * @returns {[ImmutableMap, Object]}
 */
export const recursivelySyncProps = (design) => {
  const unmergedDesign = Map.isMap(design) ? design.toJS() : cloneDeep(design);

  const syncProps = ({ desktop, mobile, sync = [] }, pathPrefix = "", depthPrefix = []) => {
    Object.keys(desktop).forEach((propName) => {
      const desktopValue = get(desktop, propName);
      const mobileValue = get(mobile, propName);
      if (isEqual(desktopValue, mobileValue)) {
        if (typeof desktopValue === "object" && !Array.isArray(desktopValue)) {
          const newPathPrefix = propName.split(".")[0];
          const newDepthPrefix = [...depthPrefix, newPathPrefix];

          syncProps(
            { desktop: desktopValue, mobile: mobileValue, sync },
            newPathPrefix,
            newDepthPrefix
          );
          // cleanup empty objects
          if (isEmpty(mobileValue)) {
            unset(mobile, propName);
          }
        } else {
          const preparedPropName = `${pathPrefix ? `${depthPrefix.join(".")}.` : ""}${propName}`;
          !sync.includes(preparedPropName) && sync.push(preparedPropName);
          unset(mobile, propName);
        }
      }
    });
  };

  const sync = (designPart) => {
    const { desktop, mobile, sync = [] } = designPart;
    const newSync = [...sync];
    syncProps({ desktop, mobile, sync: newSync });
    designPart.sync = newSync;
  };

  const traverseDesign = (designPart) => {
    if (designPart?.desktop && designPart?.mobile) {
      sync(designPart);
    }
    for (const key in designPart) {
      const value = designPart[key];
      if (typeof value === "object" && !Array.isArray(value)) {
        traverseDesign(value);
      }
    }
  };

  traverseDesign(unmergedDesign);
  return [fromJS(unmergedDesign), unmergedDesign];
};

/**
 * Checks propInfo value to see if it should be unset.
 * @param {*} value
 * @returns {boolean}
 */
export const isUnsetterValue = (value) =>
  (isEmpty(value) || isNull(value) || value === undefined) && !isNumber(value);

/**
 * Applies a typical propInfo set to the theme (as in doesn't need special cases to for application)
 * @param {ImmutableMap} theme - immutable theme
 * @param {{ name: string, value: any }} propInfo
 * @returns
 */
export const applyPropInfoToTheme = (theme, { name, value }) => {
  const key = name.split(".");
  return isUnsetterValue(value) ? theme.deleteIn(key) : theme.setIn(key, value);
};

/**
 * Removes a theme styles type's link style when the incoming value is equal it's parent's equivalent prop value, allowing the theme type to inherit instead.
 * Deals with colors, specifically.
 * @param {ImmutableMap} theme - immutable theme
 * @param {propInfo} propInfo
 * @returns {ImmutableMap} - the updated immutable theme
 */
export const handleThemeLinkInheritanceFromPropInfo = (theme, propInfo) => {
  const { name, value } = propInfo[0];
  const path = name.split(".");
  const type = theme.getIn(path.slice(0, 2));
  const propName = path[path.length - 1];
  const isColor = ["c", "hC"].includes(propName);
  const linkPath = path.slice(0, path.length - 1);
  let updatedTheme = theme;

  if (isColor && value) {
    // map to c if link property is hC
    const [color, opacity] = value.split(":");
    const isLinkHoverColor = propName === "hC" || path.includes("link");
    const isHoverColor = propName === "hC" || path.includes("hover");
    const parentPropName = isHoverColor ? "c" : propName;
    const isPropSyncedInParent = type.get(SYNCED_PROPS, List()).indexOf(parentPropName) > -1;
    const parentLayout = isPropSyncedInParent ? DESKTOP : path.includes(DESKTOP) ? DESKTOP : MOBILE;
    const inheritableColorValue = type.getIn([parentLayout, parentPropName]);

    if (!isHoverColor) {
      updatedTheme =
        inheritableColorValue === value ? theme.deleteIn(path) : theme.setIn(path, value);
    } else {
      if (inheritableColorValue === color) {
        if (opacity !== undefined && isLinkHoverColor) {
          updatedTheme = opacity === "80" ? theme.deleteIn(path) : theme.setIn(path, value);
        } else {
          updatedTheme = theme.setIn(path, value);
        }
      } else {
        updatedTheme = theme.setIn(path, value);
      }
    }
  } else {
    updatedTheme = applyPropInfoToTheme(theme, { name, value });
  }

  // remove link object from theme styles type if empty
  return updatedTheme.getIn(linkPath) && updatedTheme.getIn(linkPath).size === 0
    ? updatedTheme.deleteIn(linkPath)
    : updatedTheme;
};

/**
 * Toggles the sync state for a particular prop
 * @param {Object} propContainer
 * @param {String} activeLayout "mobile" or "desktop"
 * @param {String} nestedPropPath - path to the nested prop
 * @param {String} dynamicPropPrefix
 * @param {Object} inheritFrom - Thing to inherit from
 * @returns
 */
export const prepareSyncPropUpdates =
  (
    propContainer,
    activeLayout = DESKTOP,
    nestedPropPath = "",
    dynamicPropPrefix = "",
    inheritFrom = undefined,
  ) =>
  /**
   * Function returned by toggleSyncedProp
   * @param {String} prop - PropName to toggle sync
   * @param {String=} initialPathPrefix - Prefix for prop name
   * @param {(String|Object)=} mergeValueOverride - Value that overwrites value from propContainer global state
   */
  (prop, initialPathPrefix = "", mergeValueOverride) => {
    const getDynamicPropPrefix = () => (dynamicPropPrefix ? `${dynamicPropPrefix}.` : "");
    const pathPrefix = typeof initialPathPrefix === "string" ? initialPathPrefix : "";
    const updates = [];
    const prevSyncedProps = propContainer.get(SYNCED_PROPS);
    const syncedProps = prevSyncedProps || List();
    const propPath = `${dynamicPropPrefix ? `${dynamicPropPrefix}.` : ""}${prop}`.split(".");
    const dynamicPropPathProp = dynamicPropPrefix ? [prop.split(".")[0]] : prop;
    const pathPrefixPath = pathPrefix.split(".", []);
    const pathPrefixArray = getNestedPropPathArray(nestedPropPath);

    const propRelationships = getElementSubPropGroups(resolveElementType(propContainer));
    const hasSubProps = propRelationships[prop];
    const getSubPropPath = (subProp) => `${getDynamicPropPrefix()}${subProp}`.split(".");
    const propName = `${
      nestedPropPath ? `.${nestedPropPath}` : ""
    }${getDynamicPropPrefix()}${prop}`;
    const synced = isSynced(propContainer, propName, activeLayout, inheritFrom);

    // SPLIT
    if (synced) {
      // write inherited props to desktop/mobile if inherited
      if (
        inheritFrom &&
        (!isSynced(propContainer, prop) ||
          shouldPropInherit(propContainer, activeLayout, prop, inheritFrom))
      ) {
        const [baseContainer, inheritPropName] = prepForInherit(
          propContainer,
          activeLayout,
          prop,
          inheritFrom
        );
        const value = getProp(baseContainer, activeLayout, inheritPropName);
        [DESKTOP, MOBILE].forEach((layout) =>
          updates.push({
            value,
            name: `${pathPrefix}${layout}.${propName}`,
          })
        );
      } else {
        const syncedIndex = syncedProps.indexOf(propName);
        const reducedSyncedProps = syncedProps.delete(syncedIndex);
        // Copy desktop value to mobile
        updates.push({
          value: propContainer.getIn([DESKTOP, ...pathPrefixArray, ...propPath]),
          name: `${pathPrefix}${MOBILE}.${propName}`,
        });
        // Remove the property from the sync array
        updates.push({
          value: reducedSyncedProps.size ? reducedSyncedProps : null,
          name: `${pathPrefix}${SYNCED_PROPS}`,
          action: reducedSyncedProps.size ? "set" : false,
        });
        if (hasSubProps) {
          propRelationships[dynamicPropPathProp].forEach((subProp) => {
            const subPropPath = getSubPropPath(subProp);
            if (propContainer.getIn([DESKTOP, ...subPropPath])) {
              updates.push({
                value: propContainer.getIn([DESKTOP, ...subPropPath]),
                name: `${pathPrefix}${MOBILE}.${getDynamicPropPrefix()}${subProp}`,
              });
            }
          });
        }
      }
      // MERGE
    } else {
      const updatedSyncProps = syncedProps.push(propName);
      const mergedProps = propContainer;
      const mergeValue = mergedProps.getIn([...pathPrefixPath, activeLayout, ...propPath]);

      const addBaseSyncUpdates = (value) => {
        updates.push({
          value,
          name: `${pathPrefix}${DESKTOP}.${propName}`,
        });
        // Set placeholder, otherwise merge will ignore the list value on set
        !prevSyncedProps && updates.push(PLACEHOLDER);
        // Delete the prop from mobile since it will inherit from desktop
        updates.push({
          value: null,
          name: `${pathPrefix}${MOBILE}.${propName}`,
        });
        // Add the property to the sync array
        updates.push({
          value: List(updatedSyncProps),
          name: `${pathPrefix}${SYNCED_PROPS}`,
          action: "set",
        });
      };

      if (inheritFrom && shouldPropInherit(propContainer, activeLayout, prop, inheritFrom)) {
        const [baseContainer, inheritPropName] = prepForInherit(
          propContainer,
          activeLayout,
          prop,
          inheritFrom
        );
        const value = getProp(baseContainer, activeLayout, inheritPropName);
        addBaseSyncUpdates(value);
      } else {
        addBaseSyncUpdates(mergeValueOverride ?? mergeValue);
        if (hasSubProps) {
          propRelationships[dynamicPropPathProp].forEach((subProp) => {
            const mergedPath = getSubPropPath(subProp);
            if (activeLayout === MOBILE && mergedProps.getIn([activeLayout, ...mergedPath])) {
              updates.push({
                value: mergedProps.getIn([MOBILE, ...mergedPath]),
                name: `${pathPrefix}${DESKTOP}.${getDynamicPropPrefix()}${subProp}`,
              });
            }
            updates.push({
              value: null,
              name: `${pathPrefix}${MOBILE}.${getDynamicPropPrefix()}${subProp}`,
            });
          });
        }
      }
    }

    return updates;
  };

/**
 * Toggles the sync state for a particular prop
 * @param {Object} propContainer
 * @param {Function} onChange - onchange function for the propContainer. An array of updates are fed to this at the end of the process.
 * @param {String} activeLayout "mobile" or "desktop"
 * @param {String} nestedPropPath - path to the nested prop
 * @param {String} dynamicPropPrefix
 * @param {Object} inheritFrom - Thing to inherit from
 * @returns
 */
export const toggleSyncedProp =
  (
    propContainer,
    onChange,
    activeLayout = DESKTOP,
    nestedPropPath = "",
    dynamicPropPrefix = "",
    inheritFrom = undefined,
  ) =>
  /**
   * Function returned by toggleSyncedProp
   * @param {String} prop - PropName to toggle sync
   * @param {String=} initialPathPrefix - Prefix for prop name
   * @param {(String|Object)=} mergeValueOverride - Value that overwrites value from propContainer global state
   * @returns {Update[]} - Array of updates to be applied to the propContainer
   */
  (prop, initialPathPrefix = "", mergeValueOverride) => {
    const updates = prepareSyncPropUpdates(propContainer, activeLayout, nestedPropPath, dynamicPropPrefix, inheritFrom)(prop, initialPathPrefix, mergeValueOverride);
    return onChange(updates);
  };

  /**
   *
   * @param {Object} propContainer
   * @param {String} activeLayout "mobile" or "desktop"
   * @param {String} nestedPropPath - path to the nested prop
   * @param {String} dynamicPropPrefix
   * @param {Object} inheritFrom - Thing to inherit from
   * @returns {ToggleSyncedPropMultipleCallback}
   */
  export const toggleSyncedPropMultiple =
  (
    propContainer,
    activeLayout = DESKTOP,
    nestedPropPath = "",
    dynamicPropPrefix = "",
    inheritFrom = undefined,
  ) =>
  (props, initialPathPrefix = "", mergeValueOverride) => {
    const primaryPropSynced = isSynced(propContainer, props[0], activeLayout, inheritFrom);
    const prepareUpdates = prepareSyncPropUpdates(propContainer, activeLayout, nestedPropPath, dynamicPropPrefix, inheritFrom);

    const updates = props
      .flatMap((prop) => prepareUpdates(prop, initialPathPrefix, mergeValueOverride));

    if (!updates.length) return [];

    const firstSyncedUpdate = updates.find((update) =>
      update.name.includes(SYNCED_PROPS) && update.value !== PLACEHOLDER.value);

    const filteredUpdates = updates.filter((update) => !update.name.includes(SYNCED_PROPS));
    if (!filteredUpdates.length) return ensureArray(firstSyncedUpdate).filter(Boolean);
    if (!firstSyncedUpdate) return filteredUpdates;

    const syncArray = List.isList(firstSyncedUpdate.value)
      ? firstSyncedUpdate.value
      : List(firstSyncedUpdate.value ?? []);

    firstSyncedUpdate.value = !primaryPropSynced
      ? List(OrderedSet(syncArray.concat(props)))
      : syncArray.filter((prop) => !props.includes(prop));

    return [...filteredUpdates, firstSyncedUpdate];
  };

/**
 * Applies theme background color to entity and alters sync array where appropriate
 * @param {Object} baseEntity
 * @param {ImmutableMap} theme
 * @returns
 */
export const applyThemeBG = (baseEntity, theme) => {
  const entity = cloneDeep(baseEntity);

  const isBGSynced = isSynced(theme, THEME_BG_PROPERTY_COLOR);
  const themeDesktopBG = theme.getIn([DESKTOP, THEME_BG_PROPERTY_COLOR], "#FFFFFF");
  const themeMobileBG = isBGSynced
    ? themeDesktopBG
    : theme.getIn([MOBILE, THEME_BG_PROPERTY_COLOR], "#FFFFFF");

  set(entity, [DESKTOP, BG_PROPERTY_FILL_TYPE], BG_TYPE_COLOR);
  set(entity, [MOBILE, BG_PROPERTY_FILL_TYPE], BG_TYPE_COLOR);
  set(entity, [DESKTOP, BG_PROPERTY_BG_COLOR], themeDesktopBG);
  set(entity, [MOBILE, BG_PROPERTY_BG_COLOR], themeMobileBG);

  if (!isBGSynced) {
    const fillTypeIndex = entity.sync?.indexOf(BG_PROPERTY_FILL_TYPE);
    fillTypeIndex !== -1 && entity.sync?.splice(fillTypeIndex, 1);
  }

  return entity;
};


/**
 * Resolves the element type from a prop container, handling both Immutable Maps and plain objects.
 * @param {ImmutableMap|Object} propContainer
 * @returns {string|undefined}
 */
const resolveElementType = (propContainer) =>
  Map.isMap(propContainer) ? propContainer.get("type") : propContainer?.type;

/**
 * Returns SUB_PROP_GROUPS filtered to only include parent props that apply to the given element type.
 * Uses MERGE_PROPS to determine which parent relationships are valid for each type.
 * @param {string} elementType
 * @returns {Object}
 */
const getElementSubPropGroups = memoize((elementType => {
  const allowedParents = MERGE_PROPS[elementType];
  if (!allowedParents) return SUB_PROP_GROUPS;

  return Object.fromEntries(
    Object.entries(SUB_PROP_GROUPS).filter(([parent]) => allowedParents.includes(parent)),
  );
}));

/**
 * Sets up syncUtils for use inside an element's property panel.
 * @param {ImmutableMap} propContainer
 * @param {*} onChange
 * @param {String} activeLayout
 * @param {String} nestedPropPath
 * @param {String} dynamicPropPrefix
 * @param {ImmutableMap} inheritFrom
 * @param {Object=} dynamicChildProps - Optional map of parent prop to child props array
 * @returns
 */
export const elementSyncUtils = (
  propContainer,
  onChange,
  activeLayout,
  nestedPropPath,
  dynamicPropPrefix,
  inheritFrom,
  dynamicChildProps = {},
) => {
  /**
   * Checks if a prop should behave as synced based on parent prop sync state
   * @param {String} prop - The prop to check
   * @param {String=} parentProp - Optional parent prop to check against
   * @returns {Boolean}
   */
  const isSyncedWithParent = (prop, parentProp) => {
    if (isSynced(propContainer, prop, activeLayout, inheritFrom)) {
      return true;
    }
    if (parentProp && isSynced(propContainer, parentProp, activeLayout, inheritFrom)) {
      return true;
    }

    // Check if prop is a child of any synced parent (filtered by element type + dynamic)
    const propRelationships = {
      ...getElementSubPropGroups(resolveElementType(propContainer)),
      ...dynamicChildProps,
    };
    return Object.entries(propRelationships).some(
      ([parent, children]) =>
        children.includes(prop) && isSynced(propContainer, parent, activeLayout, inheritFrom)
    );
  };

  const getPropName = (propName, parentProp) => {
    const synced =
      typeof parentProp === "boolean" ? parentProp : isSyncedWithParent(propName, parentProp);
    return `${synced ? DESKTOP : activeLayout}.${propName}`;
  };

  return {
    syncedProps: getSyncedProps(propContainer),
    toggleSyncedProp: toggleSyncedProp(
      propContainer,
      onChange,
      activeLayout,
      nestedPropPath,
      dynamicPropPrefix,
      inheritFrom,
    ),
    toggleSyncedPropMultiple: toggleSyncedPropMultiple(
      propContainer,
      activeLayout,
      nestedPropPath,
      dynamicPropPrefix,
      inheritFrom,
    ),
    isSynced: isSyncedWithParent,
    dynamicPropPrefix,
    getPropName,
  };
};

/**
 * REEEEsync utils
 */

/**
 * Adds updates to remove props from the provided layout
 * @param {SyncableEntity} entity - Anything that can be split/merged. Likely an element.
 * @param {Array<string>} propKeys - List of valid prop names that can be removed
 * @param {"mobile" | "desktop"} removePropLayout
 * @returns {Array<PropUpdate>}
 */
export const generateRemovePropUpdatesImmutable = (entity, propKeys, removePropLayout) =>
  propKeys.reduce((result, key) => {
    const propName = [removePropLayout, ...key.split(".")];
    entity.hasIn(propName) &&
      result.push({
        name: propName.join("."),
        value: null,
        action: UPDATE_ACTION_DELETE,
      });

    return result;
  }, []);

/**
 * Adds updates to remove props from the provided layout
 * @param {SyncableEntity} entity - Anything that can be split/merged. Likely an element.
 * @param {Array<string>} propKeys - List of valid prop names that can be removed
 * @param {"mobile" | "desktop"} removePropLayout
 * @returns {Array<PropUpdate>}
 */
export const generateRemovePropUpdatesJS = (entity, propKeys, removePropLayout) =>
  propKeys.reduce((result, prop) => {
    const propName = [removePropLayout, prop].join(".");
    hasIn(entity, propName) &&
      result.push({
        name: propName,
        value: null,
        action: UPDATE_ACTION_DELETE,
      });

    return result;
  }, []);

// todo: tests for these. Maybe move to inheritance utils instead?

/**
 * Adds updates to remove props from the provided layout
 * @param {SyncableEntity} entity - Anything that can be split/merged. Likely an element.
 * @param {Array<string>} propKeys - List of valid prop names that can be removed
 * @param {"mobile" | "desktop"} removePropLayout
 * @param {Array<PropUpdate>} updater - Could be any updater, but most likely a BlockUpdate.
 */
export const generateRemovePropUpdates = (entity, ...args) =>
  Map.isMap(entity)
    ? generateRemovePropUpdatesImmutable(entity, ...args)
    : generateRemovePropUpdatesJS(entity, ...args);

/**
 * Simplified helper for creating sync relationships between parent and child props
 * @param {string} parentProp - The parent prop name
 * @param {string[]} childProps - Array of child prop names
 * @returns {Object} - Object suitable for dynamicChildProps parameter

 */
export const createSyncRelationship = (parentProp, childProps) => ({
  [parentProp]: childProps,
});

/**
 * Creates multiple sync relationships at once
 * @param {Object} relationships - Object where keys are parents and values are child arrays
 * @returns {Object} - Combined relationships object
 */
export const createSyncRelationships = (relationships) => relationships;
